import { useCallback } from 'react';

import { CfsApi } from '@/api';
import { LocalStorageItems } from '@/constants';
import { CFSEvent } from '@/models';
import type { ICFS } from '@/models';
import { useAgencyStore, useToastStore, useUserStore } from '@/store';

import { buildCFSReportDefinition } from '../reports/cfsReport/buildCFSReportDefinition';

export const useCFSPrint = () => {
  const { user } = useUserStore();
  const { currentAgency, fetchAgency, agencies } = useAgencyStore();
  const { updateToast } = useToastStore();

  const handlePrint = useCallback(
    async (
      cfsId: string,
      cfs?: ICFS | null,
      showLoader?: () => void,
      hideLoader?: () => void,
    ) => {
      if (!cfsId) {
        updateToast({ open: true, message: 'Invalid CFS ID' });
        return;
      }

      showLoader?.();

      try {
        // 1. Fetch CFS data if not provided
        const cfsData = cfs?._id ? cfs : (await CfsApi.getOne(cfsId)).data;

        // 2. Load agency
        let agencyData = currentAgency;
        if (!agencyData?._id) {
          const agencyId = localStorage.getItem(
            LocalStorageItems.CurrentAgencyId,
          );
          if (agencyId) {
            agencyData = (await fetchAgency(agencyId)) ?? null;
          }
        }

        // 3. Fetch sub-data in parallel (allSettled so one failure doesn't block print)
        const [
          namesResult,
          vehiclesResult,
          narrativesResult,
          closeReasonsResult,
          timelineResult,
        ] = await Promise.allSettled([
          CfsApi.getCfsMasterNames(cfsId),
          CfsApi.getCfsMasterVehicles(cfsId),
          CfsApi.getLinkedNarratives(cfsId, {}),
          CfsApi.getCloseReasons(),
          CfsApi.getCfsHistory(cfsId, {
            limit: 1000,
            sort: JSON.stringify({ createdAt: -1 }),
            filter: JSON.stringify({
              event: {
                $in: [
                  CFSEvent.CREATED,
                  CFSEvent.UNIT_SHIFT_ASSIGNED,
                  CFSEvent.UNIT_SHIFT_IS_EN_ROUTE,
                  CFSEvent.UNIT_SHIFT_IS_ON_SCENE,
                  CFSEvent.CLOSED,
                ],
              },
            }),
          }),
        ]);

        const cfsNames =
          namesResult.status === 'fulfilled'
            ? namesResult.value.data || []
            : [];
        const cfsVehicles =
          vehiclesResult.status === 'fulfilled'
            ? vehiclesResult.value.data || []
            : [];
        const narratives =
          narrativesResult.status === 'fulfilled'
            ? narrativesResult.value.data || []
            : [];
        const cfsCloseReasons =
          closeReasonsResult.status === 'fulfilled'
            ? closeReasonsResult.value.data || []
            : [];
        const cfsTimelineLogs =
          timelineResult.status === 'fulfilled'
            ? timelineResult.value.data?.results || []
            : [];
        // 4. Build PDF definition
        const pdfDef = await buildCFSReportDefinition({
          ctx: {
            cfs: cfsData,
            agency: agencyData as any,
            agencies,
            user,
            cfsNames,
            cfsVehicles,
            narratives,
            cfsCloseReasons,
            cfsTimelineLogs,
            printedAt: new Date(),
          },
        });

        // 5. Lazy-load pdfmake + fonts
        const pdfMakeModule = await import('pdfmake/build/pdfmake');
        const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
        const pdfMakeInstance = (pdfMakeModule as any).default || pdfMakeModule;
        const fontsInstance = (pdfFontsModule as any).default || pdfFontsModule;

        if (pdfMakeInstance && fontsInstance) {
          pdfMakeInstance.vfs = fontsInstance.pdfMake?.vfs || fontsInstance;
        }

        const createPdf = pdfMakeInstance.createPdf || pdfMakeInstance;
        const pdfDocGenerator = createPdf(pdfDef);

        // 6. Open browser print dialog
        pdfDocGenerator.print();
      } catch (err: any) {
        const message =
          'Try Again: ' +
          (typeof err === 'string'
            ? err
            : err?.response?.data?.message ||
              err?.message ||
              'Failed to print CFS Report');
        updateToast({ open: true, message });
      } finally {
        hideLoader?.();
      }
    },
    [user, currentAgency, fetchAgency, agencies, updateToast],
  );

  return { handlePrint };
};
